// Right-side floating island navbar for the canvas PROJECT page (/<owner>/<key>).
// ONLY mounts on project canvas — never on /workspaces/* (guarded in main.js).
// Mirrors WorkspaceNavbar (left-side create pill) in visual treatment — same
// glass theme, vertical pill at viewport center-right.
//
// Islands (top → bottom):
//   PagesIsland    — active canvas page + dropdown to switch pages
//   ModesIsland    — Default / Inspect / Pan mode toggle
//   PublishIsland  — Publish button + status; wires to sdk.publishVersion
//   EnvsIsland     — dev/staging/prod env badge
//   HistoryIsland  — Undo / Redo / History panel toggle; wires to el.sdk
//   SearchIsland   — opens CommandBar search (⌘K)
//   MembersIsland  — avatar stack + invite; wires to sdk.getProjectMembers
//   SharingIsland  — share / invite link
//
// Responsive: collapses to a single dot-menu below 820 px viewport width.
//
// Design tokens used:
//   round: 'C1'        – pill rounding matching left navbar
//   theme: 'canvas-card-glass' – frosted glass (dark/light handled by DS)
//   padding: 'V'       – standard navbar padding
//   gap: 'V'           – spacing between islands
//   zIndex: 30         – same layer as WorkspaceNavbar

// ── helpers ─────────────────────────────────────────────────────────────────

// Icon-only round button — reusable island atom.
const islandBtn = (title, text, onClick) => ({
  tag: 'button',
  type: 'button',
  width: '36px',
  height: '36px',
  round: '50%',
  border: '0',
  color: 'white',
  fontSize: 'Z',
  fontWeight: '400',
  lineHeight: '1',
  cursor: 'pointer',
  title,
  'aria-label': title,
  text,
  display: 'flex',
  align: 'center center',
  background: 'transparent',
  ':hover': { background: 'gray1+4' },
  onClick
})

// Hairline divider between island groups.
const divider = {
  tag: 'hr',
  width: '24px',
  height: '1px',
  border: '0',
  background: 'gray1+10',
  margin: '0',
  flexShrink: '0'
}

// Resolve projectId from root state (set by canvas bootstrap on project page).
const getProjectId = (s) => s.root?.projectId || s.root?.data?._id || s.root?.data?.id || null

// ── component ────────────────────────────────────────────────────────────────

export const CanvasMainNavbar = {
  position: 'absolute',
  top: '50%',
  right: 'V',
  transform: 'translateY(-50%)',
  zIndex: 30,
  padding: 'V',
  round: 'C1',
  theme: 'canvas-card-glass',
  flow: 'y',
  gap: 'V',
  align: 'center',
  pointerEvents: 'auto',

  // ── Pages island ──────────────────────────────────────────────────────────
  // Shows the active canvas page name; clicking opens a dropdown to switch.
  // Wires to el.getActiveCanvasPage() / el.openCanvasPage() / el.getCanvasPages().
  PagesIsland: {
    display: 'flex',
    flow: 'y',
    gap: 'W',
    align: 'center',

    PagesLabel: {
      tag: 'span',
      fontSize: 'Z',
      color: 'gray',
      text: 'PAGE',
      letterSpacing: '.04em',
      fontWeight: '700',
      userSelect: 'none'
    },

    PagesBtn: {
      tag: 'button',
      type: 'button',
      minWidth: '56px',
      maxWidth: '80px',
      padding: 'W A',
      round: 'Z',
      border: '0',
      background: 'gray1+4',
      color: 'white',
      fontSize: 'Y1',
      fontWeight: '600',
      cursor: 'pointer',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      title: 'Switch canvas page',
      'aria-label': 'Switch canvas page',
      ':hover': { background: 'gray1+8' },
      text: (el) => el.call('getActiveCanvasPage') || 'Default',
      onClick: (e, el, s) => {
        e.stopPropagation()
        // Toggle the pages dropdown panel.
        s.root?.update({ canvasPagesOpen: !s.root?.canvasPagesOpen })
      }
    },

    // Pages dropdown panel — lists available canvas pages.
    PagesPanel: {
      display: (el, s) => (s.root?.canvasPagesOpen ? 'flex' : 'none'),
      position: 'absolute',
      top: '0',
      right: '56px',
      minWidth: '160px',
      maxHeight: 'K',
      overflow: 'hidden auto',
      padding: 'V',
      round: 'Z1',
      theme: 'canvas-card-glass',
      flow: 'y',
      gap: 'W',

      PanelTitle: {
        tag: 'span',
        text: 'Pages',
        fontSize: 'Z1',
        fontWeight: '700',
        color: 'white',
        padding: '0 0 W'
      },

      PagesList: {
        flow: 'y',
        gap: 'W',
        onRender: (el, s) => {
          // Build page list from root state. Canvas pages are stored under
          // getRootState().canvas.pages (keyed by page title).
          const rootState = el.getRootState?.()
          const pages = rootState?.canvas?.pages
          const pageKeys = pages ? Object.keys(pages) : []
          const all = ['Default', ...pageKeys.filter((k) => k !== 'Default')]
          const active = el.call('getActiveCanvasPage') || 'Default'

          // Clear existing children before rebuilding.
          if (el.node) el.node.innerHTML = ''

          all.forEach((key) => {
            const isActive = key === active
            const item = document.createElement('button')
            item.type = 'button'
            item.textContent = key
            item.style.cssText = [
              'display:block',
              'width:100%',
              'text-align:left',
              'padding:4px 8px',
              'border:0',
              'border-radius:4px',
              'font-size:12px',
              'font-weight:' + (isActive ? '700' : '400'),
              'cursor:pointer',
              'background:' + (isActive ? 'rgba(255,255,255,0.1)' : 'transparent'),
              'color:white'
            ].join(';')
            item.addEventListener('click', (e) => {
              e.stopPropagation()
              el.call('openCanvasPage', key)
              s.root?.update({ canvasPagesOpen: false })
            })
            el.node.appendChild(item)
          })
        }
      },

      ClosePages: {
        tag: 'button',
        type: 'button',
        text: 'Close',
        padding: 'W A',
        round: 'Z',
        border: '0',
        background: 'transparent',
        color: 'gray',
        fontSize: 'Z',
        cursor: 'pointer',
        textAlign: 'right',
        ':hover': { color: 'white' },
        onClick: (e, el, s) => {
          e.stopPropagation()
          s.root?.update({ canvasPagesOpen: false })
        }
      }
    }
  },

  Divider0: { ...divider },

  // ── Modes island ──────────────────────────────────────────────────────────
  // Default / Inspect / Pan mode toggle. Wires to el.setCanvasMode / el.isCanvasMode.
  ModesIsland: {
    display: 'flex',
    flow: 'y',
    gap: 'W',
    align: 'center',

    ModesLabel: {
      tag: 'span',
      fontSize: 'Z',
      color: 'gray',
      text: 'MODE',
      letterSpacing: '.04em',
      fontWeight: '700',
      userSelect: 'none'
    },

    ModeDefault: {
      tag: 'button',
      type: 'button',
      title: 'Default mode',
      'aria-label': 'Default mode',
      text: '↖',
      width: '28px',
      height: '28px',
      round: 'Z',
      border: '0',
      fontSize: 'Z1',
      cursor: 'pointer',
      display: 'flex',
      align: 'center center',
      background: (el) => el.call('isCanvasMode', 'defaultMode') ? 'blue.3' : 'transparent',
      color: (el) => el.call('isCanvasMode', 'defaultMode') ? 'blue' : 'gray',
      ':hover': { background: 'gray1+6' },
      onClick: (e, el) => {
        e.stopPropagation()
        el.call('setCanvasMode', 'defaultMode')
      }
    },

    ModeInspect: {
      tag: 'button',
      type: 'button',
      title: 'Inspect mode',
      'aria-label': 'Inspect mode',
      text: '⌖',
      width: '28px',
      height: '28px',
      round: 'Z',
      border: '0',
      fontSize: 'Z1',
      cursor: 'pointer',
      display: 'flex',
      align: 'center center',
      background: (el) => el.call('isCanvasMode', 'inspectMode') ? 'purple.2' : 'transparent',
      color: (el) => el.call('isCanvasMode', 'inspectMode') ? 'purple' : 'gray',
      ':hover': { background: 'gray1+6' },
      onClick: (e, el) => {
        e.stopPropagation()
        el.call('setCanvasMode', 'inspectMode')
      }
    },

    ModePan: {
      tag: 'button',
      type: 'button',
      title: 'Pan mode',
      'aria-label': 'Pan mode',
      text: '✥',
      width: '28px',
      height: '28px',
      round: 'Z',
      border: '0',
      fontSize: 'Z1',
      cursor: 'pointer',
      display: 'flex',
      align: 'center center',
      background: (el) => el.call('isCanvasMode', 'panningMode') ? 'green.2' : 'transparent',
      color: (el) => el.call('isCanvasMode', 'panningMode') ? 'green' : 'gray',
      ':hover': { background: 'gray1+6' },
      onClick: (e, el) => {
        e.stopPropagation()
        el.call('setCanvasMode', 'panningMode')
      }
    }
  },

  Divider1: { ...divider },

  // ── Publish island ────────────────────────────────────────────────────────
  // Green pill: "Publish". On canvas project pages wires to sdk.publishVersion.
  PublishIsland: {
    display: 'flex',
    flow: 'y',
    gap: 'W',
    align: 'center',

    PublishBtn: {
      tag: 'button',
      type: 'button',
      height: '36px',
      minWidth: '72px',
      round: 'C1',
      border: '0',
      theme: 'success',
      color: 'white',
      fontSize: 'Z1',
      fontWeight: '700',
      padding: 'W A',
      cursor: 'pointer',
      title: 'Publish project',
      'aria-label': 'Publish project',
      text: 'Publish',
      ':hover': { opacity: '.88' },
      ':active': { opacity: '.78' },
      onClick: async (e, el, s) => {
        e.stopPropagation()
        const sdk = el.context?.methods?.sdk
        const projectId = getProjectId(s)
        if (!sdk?.publishVersion || !projectId) {
          window.alert('Publish is not available — no project context loaded.')
          return
        }
        try {
          await sdk.publishVersion(projectId, {})
          window.alert('Project published successfully.')
        } catch (err) {
          window.alert('Publish failed: ' + (err?.message || err))
        }
      }
    },

    // Status badge — shows draft / publishing / published based on root state.
    PublishStatus: {
      tag: 'span',
      fontSize: 'Z',
      fontWeight: '500',
      textAlign: 'center',
      padding: '0 A',
      color: (el, s) => {
        const status = s.root?.publishStatus
        if (status === 'published') return 'green'
        if (status === 'publishing') return 'yellow'
        return 'gray'
      },
      text: (el, s) => {
        const status = s.root?.publishStatus
        if (status === 'published') return 'published'
        if (status === 'publishing') return 'publishing…'
        return 'draft'
      }
    }
  },

  Divider2: { ...divider },

  // ── Envs island ───────────────────────────────────────────────────────────
  // Three environment pills: dev / staging / prod.
  // Active env highlighted; switching updates root.activeEnv.
  EnvsIsland: {
    display: 'flex',
    flow: 'y',
    gap: 'W',
    align: 'center',

    EnvLabel: {
      tag: 'span',
      fontSize: 'Z',
      color: 'gray',
      text: 'ENV',
      letterSpacing: '.04em',
      fontWeight: '700',
      userSelect: 'none'
    },

    EnvDev: {
      tag: 'button',
      type: 'button',
      text: 'dev',
      title: 'Switch to development environment',
      'aria-label': 'Development environment',
      padding: 'W',
      round: 'Z',
      border: '0',
      fontSize: 'Z',
      fontWeight: '600',
      cursor: 'pointer',
      background: (el, s) => (s.root?.activeEnv || 'development') === 'development' ? 'blue.3' : 'transparent',
      color: (el, s) => (s.root?.activeEnv || 'development') === 'development' ? 'blue' : 'gray',
      ':hover': { background: 'blue.1' },
      onClick: (e, el, s) => {
        e.stopPropagation()
        s.root?.update({ activeEnv: 'development' })
      }
    },
    EnvStaging: {
      tag: 'button',
      type: 'button',
      text: 'stg',
      title: 'Switch to staging environment',
      'aria-label': 'Staging environment',
      padding: 'W',
      round: 'Z',
      border: '0',
      fontSize: 'Z',
      fontWeight: '600',
      cursor: 'pointer',
      background: (el, s) => s.root?.activeEnv === 'staging' ? 'orange.2' : 'transparent',
      color: (el, s) => s.root?.activeEnv === 'staging' ? 'orange' : 'gray',
      ':hover': { background: 'orange.1' },
      onClick: (e, el, s) => {
        e.stopPropagation()
        s.root?.update({ activeEnv: 'staging' })
      }
    },
    EnvProd: {
      tag: 'button',
      type: 'button',
      text: 'prod',
      title: 'Switch to production environment',
      'aria-label': 'Production environment',
      padding: 'W',
      round: 'Z',
      border: '0',
      fontSize: 'Z',
      fontWeight: '600',
      cursor: 'pointer',
      background: (el, s) => s.root?.activeEnv === 'production' ? 'green.2' : 'transparent',
      color: (el, s) => s.root?.activeEnv === 'production' ? 'green' : 'gray',
      ':hover': { background: 'green.1' },
      onClick: (e, el, s) => {
        e.stopPropagation()
        s.root?.update({ activeEnv: 'production' })
      }
    }
  },

  Divider3: { ...divider },

  // ── History island ────────────────────────────────────────────────────────
  // Undo / Redo wire directly to el.sdk.undo/redo. History toggle opens
  // the VersionsAside panel (same pattern as old MainCanvasNavbar.js).
  HistoryIsland: {
    display: 'flex',
    flow: 'y',
    gap: 'W',
    align: 'center',

    UndoBtn: {
      ...islandBtn('Undo  ⌘Z', '↶', async (e, el) => {
        e.stopPropagation()
        try { await el.sdk.undo() } catch {}
      })
    },
    RedoBtn: {
      ...islandBtn('Redo  ⌘⇧Z', '↷', async (e, el) => {
        e.stopPropagation()
        try { await el.sdk.redo() } catch {}
      })
    },
    HistoryBtn: {
      ...islandBtn('Version history', '◷', (e, el, s) => {
        e.stopPropagation()
        const rootState = el.getRootState?.()
        if (rootState?.toggle) {
          rootState.toggle('isVersionsOpen', { preventUpdate: true })
        } else {
          s.root?.update({ isVersionsOpen: !s.root?.isVersionsOpen })
        }
      })
    }
  },

  Divider4: { ...divider },

  // ── Search island ─────────────────────────────────────────────────────────
  // Opens the canvas CommandBar / search overlay (⌘K).
  SearchIsland: {
    display: 'flex',
    flow: 'y',
    align: 'center',

    SearchBtn: {
      ...islandBtn('Search  ⌘K', '⌕', (e, el) => {
        e.stopPropagation()
        if (el.call('isCanvasV2')) {
          el.call('toggleShortcutsOverlay')
          return
        }
        const evt = new KeyboardEvent('keydown', {
          key: 'k',
          metaKey: true,
          bubbles: true,
          cancelable: true
        })
        document.dispatchEvent(evt)
      })
    }
  },

  Divider5: { ...divider },

  // ── Members island ────────────────────────────────────────────────────────
  // Shows online-member count badge and opens member management panel.
  MembersIsland: {
    display: 'flex',
    flow: 'y',
    align: 'center',

    MembersBtn: {
      tag: 'button',
      type: 'button',
      width: '36px',
      height: '36px',
      round: '50%',
      border: '0',
      color: 'white',
      fontSize: 'Z',
      cursor: 'pointer',
      title: 'Members',
      'aria-label': 'Project members',
      display: 'flex',
      align: 'center center',
      position: 'relative',
      background: 'transparent',
      ':hover': { background: 'gray1+4' },
      onClick: (e, el, s) => {
        e.stopPropagation()
        s.root?.update({ membersOpen: !s.root?.membersOpen })
      },

      Icon: {
        tag: 'span',
        text: '👥',
        fontSize: 'Z1',
        lineHeight: '1',
        pointerEvents: 'none'
      },

      CountBadge: {
        tag: 'span',
        position: 'absolute',
        top: '1px',
        right: '1px',
        minWidth: '14px',
        height: '14px',
        round: 'C1',
        background: 'blue',
        color: 'white',
        fontSize: 'Y',
        fontWeight: '700',
        display: 'flex',
        align: 'center center',
        padding: '0 3px',
        pointerEvents: 'none',
        text: (el, s) => {
          const count = s.root?.activeMembers || 0
          return count > 0 ? String(count) : ''
        },
        hide: (el, s) => !(s.root?.activeMembers > 0)
      }
    },

    MembersPanel: {
      display: (el, s) => (s.root?.membersOpen ? 'flex' : 'none'),
      position: 'absolute',
      top: '0',
      right: '56px',
      minWidth: '220px',
      maxHeight: 'K',
      overflow: 'hidden auto',
      padding: 'V',
      round: 'Z1',
      theme: 'canvas-card-glass',
      flow: 'y',
      gap: 'V',

      PanelTitle: {
        tag: 'span',
        text: 'Members',
        fontSize: 'Z1',
        fontWeight: '700',
        color: 'white'
      },

      MembersList: {
        flow: 'y',
        gap: 'W',
        tag: 'ul',
        padding: '0',
        margin: '0',
        style: { listStyle: 'none' },
        children: (el, s) => {
          const members = s.root?.projectMembers || []
          if (!members.length) {
            return [{ tag: 'li', text: 'No members loaded.', fontSize: 'Z', color: 'gray', padding: 'W 0' }]
          }
          return members.map((m) => ({
            tag: 'li',
            display: 'flex',
            flow: 'x',
            gap: 'A',
            align: 'center',
            padding: 'W 0',
            fontSize: 'Z',
            color: 'white',
            Avatar: {
              tag: 'span',
              width: '24px',
              height: '24px',
              round: '50%',
              background: 'blue.4',
              display: 'flex',
              align: 'center center',
              fontSize: 'Y',
              fontWeight: '700',
              color: 'white',
              text: (m.name || m.email || '?')[0].toUpperCase(),
              flexShrink: '0'
            },
            Name: {
              tag: 'span',
              text: m.name || m.email || 'Unknown',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }
          }))
        },
        onRender: async (el, s) => {
          if (el.__membersFetched) return
          const sdk = el.context?.methods?.sdk
          const projectId = getProjectId(s)
          if (!sdk?.getProjectMembers || !projectId) return
          el.__membersFetched = true
          try {
            const result = await sdk.getProjectMembers(projectId)
            const members = result?.data || result || []
            s.root?.update({ projectMembers: members })
          } catch {}
        }
      },

      InviteBtn: {
        tag: 'button',
        type: 'button',
        text: '+ Invite',
        fontSize: 'Z1',
        fontWeight: '600',
        padding: 'W A',
        round: 'Z',
        border: '0',
        background: 'blue.2',
        color: 'blue',
        cursor: 'pointer',
        ':hover': { background: 'blue.3' },
        onClick: (e, el, s) => {
          e.stopPropagation()
          s.root?.update({ membersOpen: false, sharingOpen: true })
        }
      }
    }
  },

  Divider6: { ...divider },

  // ── Sharing island ────────────────────────────────────────────────────────
  // Share icon → opens sharing/invite-link dialog.
  SharingIsland: {
    display: 'flex',
    flow: 'y',
    align: 'center',

    ShareBtn: {
      ...islandBtn('Share project', '⬆', (e, el, s) => {
        e.stopPropagation()
        s.root?.update({ sharingOpen: !s.root?.sharingOpen })
      })
    },

    SharePanel: {
      display: (el, s) => (s.root?.sharingOpen ? 'flex' : 'none'),
      position: 'absolute',
      bottom: '0',
      right: '56px',
      minWidth: '280px',
      padding: 'V',
      round: 'Z1',
      theme: 'canvas-card-glass',
      flow: 'y',
      gap: 'V',

      PanelTitle: {
        tag: 'span',
        text: 'Share',
        fontSize: 'Z1',
        fontWeight: '700',
        color: 'white'
      },

      LinkRow: {
        display: 'flex',
        flow: 'x',
        gap: 'V',
        align: 'center',

        LinkInput: {
          tag: 'input',
          type: 'text',
          readOnly: true,
          flex: '1',
          padding: 'W A',
          round: 'Z',
          borderWidth: '1px',
          borderStyle: 'solid',
          borderColor: 'line',
          background: 'gray1+3',
          color: 'white',
          fontSize: 'Z',
          value: (el) => {
            if (typeof window === 'undefined') return ''
            return window.location.href
          },
          onClick: (e, el) => { try { el.node.select() } catch {} }
        },

        CopyBtn: {
          tag: 'button',
          type: 'button',
          text: 'Copy',
          padding: 'W A',
          round: 'Z',
          border: '0',
          background: 'blue.2',
          color: 'blue',
          fontSize: 'Z',
          fontWeight: '700',
          cursor: 'pointer',
          ':hover': { background: 'blue.3' },
          onClick: async (e, el) => {
            e.stopPropagation()
            try {
              await navigator.clipboard.writeText(window.location.href)
              el.node.textContent = 'Copied!'
              setTimeout(() => { if (el.node) el.node.textContent = 'Copy' }, 1800)
            } catch {
              window.alert('Copy failed — please copy the URL manually.')
            }
          }
        }
      },

      CloseShare: {
        tag: 'button',
        type: 'button',
        text: 'Close',
        padding: 'W A',
        round: 'Z',
        border: '0',
        background: 'transparent',
        color: 'gray',
        fontSize: 'Z',
        cursor: 'pointer',
        textAlign: 'right',
        ':hover': { color: 'white' },
        onClick: (e, el, s) => {
          e.stopPropagation()
          s.root?.update({ sharingOpen: false })
        }
      }
    }
  },

  // ── Responsive collapse ───────────────────────────────────────────────────
  // Below 820 px the full pill collapses to a single DotMenu button.
  DotMenu: {
    tag: 'button',
    type: 'button',
    width: '36px',
    height: '36px',
    round: '50%',
    border: '0',
    color: 'white',
    fontSize: 'B',
    cursor: 'pointer',
    title: 'Canvas actions',
    'aria-label': 'Canvas actions menu',
    text: '⋮',
    display: 'none',
    background: 'transparent',
    ':hover': { background: 'gray1+4' },
    style: { '@media (max-width: 820px)': { display: 'flex' } },
    onClick: (e, el, s) => {
      e.stopPropagation()
      s.root?.update({ canvasRightMenuOpen: !s.root?.canvasRightMenuOpen })
    }
  }
}
